import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, FileDown, Send, Trash2, Wrench } from "lucide-react";
import { computeQuoteTotals } from "@mavatech/core";
import { DEFAULT_HANDLER_NAME, formatMoney } from "@mavatech/shared";
import { useUnsavedGuard } from "@/lib/useUnsavedGuard";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  ConfirmDialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { PositionEditor } from "@/components/positions/PositionEditor";
import { toItemsPayload, type Row } from "@/components/positions/types";

const STATUS_TONE: Record<
  string,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  draft: "neutral",
  sent: "accent",
  accepted: "success",
  rejected: "danger",
  expired: "warning",
};
const STATUS_LABEL: Record<string, string> = {
  draft: "Entwurf",
  sent: "Gesendet",
  accepted: "Angenommen",
  rejected: "Abgelehnt",
  expired: "Abgelaufen",
};

export function QuoteForm() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const org = trpc.organization.get.useQuery();
  const members = trpc.auth.members.useQuery();
  const me = trpc.auth.me.useQuery();
  const stdTexts = trpc.standardText.list.useQuery(undefined, {
    enabled: isNew,
  });
  const existing = trpc.quote.get.useQuery(
    { id: id! },
    { enabled: !isNew, retry: false },
  );

  const [customerId, setCustomerId] = useState("");
  // Händischer Empfänger (nur ohne zugeordneten Kunden) — wird im PDF-Adressblock gedruckt
  const [recipientName, setRecipientName] = useState("");
  const [recipientAddition, setRecipientAddition] = useState("");
  const [recipientStreet, setRecipientStreet] = useState("");
  const [recipientZip, setRecipientZip] = useState("");
  const [recipientCity, setRecipientCity] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [showSiteAddress, setShowSiteAddress] = useState(false);
  const properties = trpc.property.list.useQuery(
    { customerId },
    { enabled: !!customerId },
  );
  const [title, setTitle] = useState("");
  const [issueDate, setIssueDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [validUntil, setValidUntil] = useState("");
  const [taxRate, setTaxRate] = useState(20);
  const [rcBau, setRcBau] = useState(false);
  const [introText, setIntroText] = useState("");
  const [infoText, setInfoText] = useState("");
  const [closingText, setClosingText] = useState("");
  const [paymentText, setPaymentText] = useState("");
  const [legalNoteText, setLegalNoteText] = useState("");
  const [courtVenueText, setCourtVenueText] = useState("");
  const [validityText, setValidityText] = useState("");
  const [skontoPercent, setSkontoPercent] = useState(0);
  const [skontoDays, setSkontoDays] = useState(8);
  const [paymentGraceDays, setPaymentGraceDays] = useState(14);
  const [handlerUserId, setHandlerUserId] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Prefill (neu)
  useEffect(() => {
    if (!isNew || loaded) return;
    if (org.data) setTaxRate((org.data as any).defaultTaxRate ?? 20);
    if (stdTexts.data) {
      const pick = (scope: string) =>
        stdTexts.data!.find((t) => t.scope === scope && t.isDefault)?.body ??
        "";
      setIntroText(pick("quote_intro"));
      setClosingText(pick("quote_closing"));
      setPaymentText(pick("quote_payment_terms"));
      setLegalNoteText(pick("doc_legal_note"));
      setCourtVenueText(pick("doc_court_venue"));
      setValidityText(pick("quote_validity"));
      setLoaded(true);
    }
  }, [isNew, loaded, org.data, stdTexts.data]);

  // SachbearbeiterIn vorbelegen: Hauptnutzer (Ratko Mirkovic), sonst angemeldeter
  // Benutzer, sonst erster im Team.
  useEffect(() => {
    if (!isNew || handlerUserId || !members.data) return;
    const preferred =
      members.data.find(
        (m) =>
          m.name.trim().toLowerCase() === DEFAULT_HANDLER_NAME.toLowerCase(),
      )?.userId ??
      me.data?.user.id ??
      members.data[0]?.userId ??
      "";
    if (preferred) setHandlerUserId(preferred);
  }, [isNew, handlerUserId, members.data, me.data]);

  // Warenkorb aus Großhändler-Absprung (Punchout) übernehmen
  useEffect(() => {
    if (!isNew) return;
    const raw = sessionStorage.getItem("mavatech_punchout_cart");
    if (!raw) return;
    sessionStorage.removeItem("mavatech_punchout_cart");
    try {
      const lines = JSON.parse(raw) as {
        supplierArticleNumber: string;
        name: string;
        quantity: number;
        unit: string;
        unitPriceCents: number;
      }[];
      setRows(
        lines.map((l, i) => ({
          clientId: `pc_${i}_${Math.random().toString(36).slice(2)}`,
          parentClientId: null,
          kind: "position" as const,
          mode: "mandatory" as const,
          itemCategory: "material" as const,
          description: l.name,
          longText: l.supplierArticleNumber
            ? `Art.-Nr. ${l.supplierArticleNumber}`
            : "",
          quantity: l.quantity || 1,
          unit: l.unit || "Stk.",
          unitPriceCents: l.unitPriceCents || 0,
          discountPercent: 0,
          alternativeGroup: "",
          isChosenAlternative: false,
          rangeFromLabel: "",
          rangeToLabel: "",
          articleId: null,
          purchasePriceCents: null,
        })),
      );
      if (lines.length)
        addToast({
          type: "info",
          title: `${lines.length} Position(en) aus dem Warenkorb übernommen`,
        });
    } catch {
      /* ignore */
    }
  }, [isNew]);

  // Prefill (bearbeiten)
  useEffect(() => {
    if (isNew || !existing.data || loaded) return;
    const q = existing.data;
    setCustomerId(q.customer?.id ?? "");
    setRecipientName(q.recipientName ?? "");
    setRecipientAddition(q.recipientAddition ?? "");
    setRecipientStreet(q.recipientStreet ?? "");
    setRecipientZip(q.recipientZip ?? "");
    setRecipientCity(q.recipientCity ?? "");
    setPropertyId(q.propertyId ?? "");
    setShowSiteAddress(!!q.showSiteAddress);
    setTitle(q.title);
    setIssueDate(new Date(q.issueDate).toISOString().slice(0, 10));
    setValidUntil(
      q.validUntil ? new Date(q.validUntil).toISOString().slice(0, 10) : "",
    );
    setTaxRate(q.taxRatePercent);
    setRcBau(q.reverseChargeBau);
    setIntroText(q.introText ?? "");
    setInfoText(q.infoText ?? "");
    setClosingText(q.closingText ?? "");
    setPaymentText(q.paymentTermsText ?? "");
    setLegalNoteText(q.legalNoteText ?? "");
    setCourtVenueText(q.courtVenueText ?? "");
    setValidityText(q.validityText ?? "");
    setSkontoPercent(q.skontoPercent ?? 0);
    setSkontoDays(q.skontoDays ?? 8);
    setPaymentGraceDays(q.paymentGraceDays ?? 14);
    setHandlerUserId(q.handlerUserId ?? "");
    setRows(
      q.items.map((it) => ({
        clientId: it.id,
        parentClientId: it.parentId,
        kind: it.kind as Row["kind"],
        mode: it.mode as Row["mode"],
        itemCategory: (it.itemCategory as Row["itemCategory"]) ?? "material",
        description: it.description,
        longText: it.longText ?? "",
        quantity: it.quantity,
        unit: it.unit,
        unitPriceCents: it.unitPriceCents,
        discountPercent: it.discountPercent,
        alternativeGroup: it.alternativeGroup ?? "",
        isChosenAlternative: it.isChosenAlternative,
        rangeFromLabel: it.rangeFromLabel ?? "",
        rangeToLabel: it.rangeToLabel ?? "",
        articleId: it.articleId ?? null,
        purchasePriceCents: it.purchasePriceCents ?? null,
      })),
    );
    setLoaded(true);
  }, [isNew, existing.data, loaded]);

  const totals = useMemo(
    () =>
      computeQuoteTotals(
        rows.map((r, i) => ({
          id: r.clientId,
          parentId: r.parentClientId,
          sortIndex: i,
          kind: r.kind,
          quantity: r.quantity,
          unitPriceCents: r.unitPriceCents,
          discountPercent: r.discountPercent,
          mode: r.mode,
          isChosenAlternative: r.isChosenAlternative,
          rangeFromLabel: r.rangeFromLabel || null,
          rangeToLabel: r.rangeToLabel || null,
        })),
        {
          taxScheme: ((org.data as any)?.taxScheme ?? "regel") as
            "regel" | "kleinunternehmer",
          taxRatePercent: taxRate,
          reverseChargeBau: rcBau,
        },
      ),
    [rows, taxRate, rcBau, org.data],
  );

  const materialCostCents = useMemo(
    () =>
      rows.reduce(
        (sum, r) =>
          r.kind === "position" &&
          r.itemCategory === "material" &&
          r.purchasePriceCents != null
            ? sum + Math.round(r.purchasePriceCents * r.quantity)
            : sum,
        0,
      ),
    [rows],
  );

  const headerPayload = () => ({
    customerId: customerId || null,
    recipientName: customerId ? null : recipientName.trim() || null,
    recipientAddition: customerId ? null : recipientAddition.trim() || null,
    recipientStreet: customerId ? null : recipientStreet.trim() || null,
    recipientZip: customerId ? null : recipientZip.trim() || null,
    recipientCity: customerId ? null : recipientCity.trim() || null,
    propertyId: propertyId || null,
    showSiteAddress: !!propertyId && showSiteAddress,
    title: title.trim(),
    issueDate: new Date(issueDate),
    validUntil: validUntil ? new Date(validUntil) : null,
    taxRatePercent: taxRate,
    reverseChargeBau: rcBau,
    introText: introText.trim() || null,
    infoText: infoText.trim() || null,
    closingText: closingText.trim() || null,
    paymentTermsText: paymentText.trim() || null,
    legalNoteText: legalNoteText.trim() || null,
    courtVenueText: courtVenueText.trim() || null,
    validityText: validityText.trim() || null,
    skontoPercent,
    skontoDays,
    paymentGraceDays,
    handlerUserId: handlerUserId || null,
  });

  // Warnung bei ungespeicherten Eingaben (Zurück, Menü, Seite schließen …), siehe lib/useUnsavedGuard.tsx
  const guard = useUnsavedGuard({
    ready: loaded && (!isNew || !!handlerUserId),
    getSnapshot: () => JSON.stringify([headerPayload(), rows]),
  });
  const leave = (to: string) => {
    guard.allowLeave();
    navigate(to);
  };

  const create = trpc.quote.create.useMutation({
    onSuccess: (q) => {
      utils.quote.list.invalidate();
      addToast({ type: "success", title: "Angebot erstellt" });
      leave(`/angebote/${q.id}`);
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const update = trpc.quote.update.useMutation({
    onSuccess: () => {
      guard.markSaved();
      utils.quote.get.invalidate({ id: id! });
      utils.quote.list.invalidate();
      addToast({ type: "success", title: "Gespeichert" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const send = trpc.quote.send.useMutation({
    onSuccess: () => {
      utils.quote.get.invalidate({ id: id! });
      utils.quote.list.invalidate();
      utils.order.list.invalidate();
      addToast({
        type: "success",
        title: "Angebotsnummer vergeben — Auftrag angelegt (Angeboten)",
      });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const setStatus = trpc.quote.setStatus.useMutation({
    onSuccess: () => {
      utils.quote.get.invalidate({ id: id! });
      utils.quote.list.invalidate();
      utils.order.list.invalidate();
    },
  });
  const duplicate = trpc.quote.duplicate.useMutation({
    onSuccess: (q) => {
      utils.quote.list.invalidate();
      leave(`/angebote/${q.id}`);
      addToast({ type: "success", title: "Angebot dupliziert" });
    },
  });
  const remove = trpc.quote.remove.useMutation({
    onSuccess: () => {
      utils.quote.list.invalidate();
      leave("/angebote");
    },
  });
  const pdf = trpc.quote.generatePdf.useMutation({
    onSuccess: (r) => window.open(r.url, "_blank"),
    onError: (e) =>
      addToast({
        type: "error",
        title: "PDF fehlgeschlagen",
        description: e.message,
      }),
  });
  const toInvoice = trpc.invoice.createFromQuote.useMutation({
    onSuccess: (inv) => {
      utils.quote.get.invalidate({ id: id! });
      addToast({ type: "success", title: "Rechnung erstellt" });
      leave(`/rechnungen/${inv!.id}`);
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const toPartial = trpc.invoice.createPartialFromQuote.useMutation({
    onSuccess: (inv) => {
      utils.quote.get.invalidate({ id: id! });
      addToast({ type: "success", title: "Anzahlungsrechnung erstellt" });
      leave(`/rechnungen/${inv!.id}`);
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  // Kunde ist optional — ein Angebot kann zunächst ohne Kunde entstehen und später zugeordnet werden.
  const canSave = title.trim();
  const hasCustomer = !!existing.data?.customerId;
  const needCustomerHint = "Erst einen Kunden zuordnen und speichern";
  const status = existing.data?.status ?? "draft";
  const locked = status === "accepted" || status === "rejected";

  const onSave = () => {
    if (!canSave) return false;
    const items = toItemsPayload(rows);
    if (isNew) create.mutate({ ...headerPayload(), items });
    else update.mutate({ id: id!, ...headerPayload(), items });
    return true;
  };
  guard.setSave(onSave);

  // Siehe InvoiceForm.tsx — kein leeres "Entwurf"-Formular zeigen, solange ein bestehendes
  // Angebot noch lädt (AUDIT-2026-10-01-UX.md, Punkt 1.4).
  if (!isNew && existing.isLoading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Angebot" />
        <div className="flex-1 flex items-center justify-center text-sm text-text-tertiary">
          Lädt …
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" {...guard.rootProps}>
      {guard.dialog}
      <PageHeader
        title={
          isNew
            ? "Neues Angebot"
            : existing.data?.number
              ? `Angebot ${existing.data.number}`
              : "Angebot (Entwurf)"
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {!isNew && (
              <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>
            )}
            <Button variant="ghost" onClick={() => navigate("/angebote")}>
              <ArrowLeft className="w-4 h-4" /> Angebote
            </Button>
            {!isNew && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => pdf.mutate({ id: id! })}
                  loading={pdf.isPending}
                >
                  <FileDown className="w-4 h-4" /> Download
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => duplicate.mutate({ id: id! })}
                >
                  <Copy className="w-4 h-4" /> Duplizieren
                </Button>
                {existing.data?.orders?.length ? (
                  <Button
                    variant="ghost"
                    onClick={() =>
                      navigate(
                        `/auftraege?open=${existing.data!.orders[0]!.id}`,
                      )
                    }
                  >
                    <Wrench className="w-4 h-4" /> Auftrag{" "}
                    {existing.data.orders[0]!.number ?? ""}
                  </Button>
                ) : null}
                {(status === "draft" || status === "expired") && (
                  <Button
                    variant="secondary"
                    onClick={() => send.mutate({ id: id! })}
                    loading={send.isPending}
                  >
                    <Send className="w-4 h-4" /> Senden
                  </Button>
                )}
                {status === "sent" && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setStatus.mutate({ id: id!, status: "accepted" })
                      }
                    >
                      Angenommen
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setStatus.mutate({ id: id!, status: "rejected" })
                      }
                    >
                      Abgelehnt
                    </Button>
                  </>
                )}
                {(status === "sent" || status === "accepted") && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        toInvoice.mutate({ quoteId: id!, kind: "standalone" })
                      }
                      loading={toInvoice.isPending}
                      disabled={!hasCustomer}
                      title={hasCustomer ? undefined : needCustomerHint}
                    >
                      → Rechnung
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const p = window.prompt(
                          "Anzahlung in Prozent des Nettobetrags:",
                          "30",
                        );
                        const pct = p == null ? null : Number(p);
                        if (pct != null && pct > 0 && pct <= 100)
                          toPartial.mutate({
                            quoteId: id!,
                            kind: "advance",
                            percent: pct,
                          });
                      }}
                      loading={toPartial.isPending}
                      disabled={!hasCustomer}
                      title={hasCustomer ? undefined : needCustomerHint}
                    >
                      → Anzahlung
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const p = window.prompt(
                          "Teilrechnung in Prozent des Nettobetrags:",
                          "50",
                        );
                        const pct = p == null ? null : Number(p);
                        if (pct != null && pct > 0 && pct <= 100)
                          toPartial.mutate({
                            quoteId: id!,
                            kind: "partial",
                            percent: pct,
                          });
                      }}
                      loading={toPartial.isPending}
                      disabled={!hasCustomer}
                      title={hasCustomer ? undefined : needCustomerHint}
                    >
                      → Teilrechnung
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        toInvoice.mutate({ quoteId: id!, kind: "final" })
                      }
                      loading={toInvoice.isPending}
                      disabled={!hasCustomer}
                      title={hasCustomer ? undefined : needCustomerHint}
                    >
                      → Schlussrechnung
                    </Button>
                  </>
                )}
              </>
            )}
            {!locked && (
              <Button
                variant="primary"
                onClick={onSave}
                loading={create.isPending || update.isPending}
                disabled={!canSave}
              >
                Speichern
              </Button>
            )}
            {!isNew && !existing.data?.number && (
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-3 gap-4 max-w-4xl">
          <CustomerCombobox
            label="Kunde (optional)"
            value={customerId}
            onChange={(id) => {
              setCustomerId(id);
              setPropertyId("");
              setShowSiteAddress(false);
            }}
          />
          <div className="col-span-2 flex items-end gap-4">
            <div className="flex-1 min-w-0">
              <Select
                label="Objekt (optional)"
                value={propertyId}
                onChange={(e) => {
                  setPropertyId(e.target.value);
                  // Durchführungsort gleich mit einblenden, sobald ein Objekt gewählt ist (abwählbar).
                  setShowSiteAddress(!!e.target.value);
                }}
                disabled={!customerId}
                options={[
                  {
                    value: "",
                    label: customerId ? "— kein Objekt —" : "erst Kunde wählen",
                  },
                  ...(properties.data ?? []).map((p) => ({
                    value: p.id,
                    label: p.label,
                  })),
                ]}
              />
            </div>
            <label
              className={`flex items-center gap-2 text-xs leading-tight max-w-[11rem] shrink-0 pb-2.5 ${propertyId ? "text-text-secondary" : "text-text-tertiary"}`}
            >
              <input
                type="checkbox"
                checked={showSiteAddress}
                disabled={!propertyId}
                onChange={(e) => setShowSiteAddress(e.target.checked)}
              />
              Durchführungsort im Adressblock anzeigen
            </label>
          </div>
          {!customerId && (
            <div className="col-span-full grid grid-cols-6 gap-4">
              <div className="col-span-6 -mb-2 text-xs text-text-tertiary">
                Ohne Kunde: Empfänger von Hand eintragen — wird im PDF gedruckt,
                ohne dass ein Kunde angelegt wird.
              </div>
              <div className="col-span-3">
                <Input
                  label="Empfänger (Name / Firma)"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                />
              </div>
              <div className="col-span-3">
                <Input
                  label="Zusatz (z. B. z. H. Frau Muster)"
                  value={recipientAddition}
                  onChange={(e) => setRecipientAddition(e.target.value)}
                />
              </div>
              <div className="col-span-3">
                <Input
                  label="Straße und Nr."
                  value={recipientStreet}
                  onChange={(e) => setRecipientStreet(e.target.value)}
                />
              </div>
              <div className="col-span-1">
                <Input
                  label="PLZ"
                  value={recipientZip}
                  onChange={(e) => setRecipientZip(e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <Input
                  label="Ort"
                  value={recipientCity}
                  onChange={(e) => setRecipientCity(e.target.value)}
                />
              </div>
            </div>
          )}
          <div className="col-span-full">
            <Textarea
              label="Betreff (mehrzeilig möglich — erscheint im PDF unter dem Dokumenttitel)"
              rows={3}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="col-span-full">
            <Textarea
              label="Info / Notiz (erscheint im PDF unter dem Betreff — leer = nicht angezeigt)"
              rows={2}
              value={infoText}
              onChange={(e) => setInfoText(e.target.value)}
            />
          </div>
          <Input
            label="Datum"
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
          />
          <Input
            label="Gültig bis"
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
          <Select
            label="USt-Satz"
            value={String(taxRate)}
            onChange={(e) => setTaxRate(Number(e.target.value))}
            options={[
              { value: "20", label: "20 %" },
              { value: "13", label: "13 %" },
              { value: "10", label: "10 %" },
              { value: "0", label: "0 %" },
            ]}
          />
          <label className="flex items-center gap-2 text-sm text-text-secondary pb-1.5 self-end">
            <input
              type="checkbox"
              checked={rcBau}
              onChange={(e) => setRcBau(e.target.checked)}
            />
            Reverse Charge Bauleistung (§ 19 Abs. 1a)
          </label>
        </div>

        <div className="grid gap-4 max-w-4xl">
          <Textarea
            label="Einleitungstext"
            rows={3}
            value={introText}
            onChange={(e) => setIntroText(e.target.value)}
          />
        </div>

        <div className="max-w-4xl">
          <h2 className="text-sm font-semibold text-text-primary mb-2">
            Positionen
          </h2>
          <PositionEditor rows={rows} onChange={setRows} />
        </div>

        <div className="max-w-md ml-auto card p-4 space-y-1.5 text-sm">
          {!rcBau && (org.data as any)?.taxScheme !== "kleinunternehmer" ? (
            <>
              <SumRow
                label="Nettobetrag"
                value={formatMoney(totals.netCents)}
              />
              <SumRow
                label={`USt ${taxRate} %`}
                value={formatMoney(totals.taxCents)}
              />
              <SumRow
                label="Gesamtbetrag"
                value={formatMoney(totals.grossCents)}
                bold
              />
            </>
          ) : (
            <SumRow
              label="Gesamtbetrag"
              value={formatMoney(totals.netCents)}
              bold
            />
          )}
          {totals.optionalNetCents > 0 && (
            <SumRow
              label="davon optional (netto, separat)"
              value={formatMoney(totals.optionalNetCents)}
              muted
            />
          )}
          {materialCostCents > 0 && (
            <div className="border-t border-border-subtle pt-1.5 mt-1.5">
              <SumRow
                label="Materialkosten (Einkauf netto, nur intern)"
                value={formatMoney(materialCostCents)}
                muted
              />
            </div>
          )}
        </div>

        <div className="max-w-4xl card p-4 space-y-3">
          <p className="text-sm font-semibold text-text-primary">Konditionen</p>
          <Select
            label="SachbearbeiterIn"
            value={handlerUserId}
            onChange={(e) => setHandlerUserId(e.target.value)}
            options={[
              { value: "", label: "— keine —" },
              ...(members.data ?? []).map((m) => ({
                value: m.userId,
                label: m.name,
              })),
            ]}
          />
          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Skonto %"
              type="number"
              min={0}
              max={100}
              step="0.5"
              value={skontoPercent}
              onChange={(e) =>
                setSkontoPercent(parseFloat(e.target.value) || 0)
              }
            />
            <Input
              label="Skonto-Frist (Tage)"
              type="number"
              min={0}
              value={skontoDays}
              onChange={(e) => setSkontoDays(parseInt(e.target.value) || 0)}
            />
            <Input
              label="ohne Abzug (Tage)"
              type="number"
              min={0}
              value={paymentGraceDays}
              onChange={(e) =>
                setPaymentGraceDays(parseInt(e.target.value) || 0)
              }
            />
          </div>
          <p className="text-xs text-text-tertiary">
            0 % blendet die Skonto-Zeile aus. Skonto rechnet vom Bruttobetrag.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 max-w-4xl">
          <Textarea
            label="Schlusstext"
            rows={3}
            value={closingText}
            onChange={(e) => setClosingText(e.target.value)}
          />
          <Textarea
            label="Zahlungsbedingungen"
            rows={2}
            value={paymentText}
            onChange={(e) => setPaymentText(e.target.value)}
          />
          <Textarea
            label="AGB-/Gerichtsstandshinweis"
            rows={2}
            value={legalNoteText}
            onChange={(e) => setLegalNoteText(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-4">
            <Textarea
              label="Gerichtsstand"
              rows={2}
              value={courtVenueText}
              onChange={(e) => setCourtVenueText(e.target.value)}
            />
            <Textarea
              label="Gültigkeitsdauer"
              rows={2}
              value={validityText}
              onChange={(e) => setValidityText(e.target.value)}
            />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Angebot löschen?"
        message="Das Angebot wird ausgeblendet (Soft-Delete)."
        confirmLabel="Löschen"
        danger
        onConfirm={() => remove.mutate({ id: id! })}
      />
    </div>
  );
}

function SumRow({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={`flex justify-between ${bold ? "text-base font-semibold text-text-primary border-t border-border-subtle pt-2" : muted ? "text-xs text-text-tertiary" : "text-text-secondary"}`}
    >
      <span>{label}</span>
      <span className="text-money">{value}</span>
    </div>
  );
}
